import { useEffect, useState, useCallback } from "react";
import { useSalesAuth } from "../../../hooks/useSalesAuth";
import {
  Box,
  Card,
  CardContent,
  Grid2 as Grid,
  Typography,
  useTheme,
  Skeleton,
  Avatar,
  Chip,
  Divider,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import ApexChart from "react-apexcharts";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DescriptionIcon from "@mui/icons-material/Description";
import VerifiedIcon from "@mui/icons-material/Verified";
import WifiIcon from "@mui/icons-material/Wifi";
import GroupsIcon from "@mui/icons-material/Groups";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { useRef } from "react";
import { omitCustomProps } from "../../../utils/omitCustomProps";

const Chart = styled(ApexChart)``;

// Rotate animation for refresh icon
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const RotatingIcon = styled(
  RefreshIcon,
  omitCustomProps("rotating"),
)(({ rotating }) => ({
  ...(rotating && {
    animation: `${rotate} 1s linear infinite`,
  }),
}));

// Stat Card Component
const StatCard = ({
  title,
  value,
  subtitle,
  change,
  changeLabel,
  icon,
  color,
  loading,
}) => {
  const isPositive = change >= 0;

  return (
    <Card
      sx={{
        height: "100%",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          right: 0,
          width: "100px",
          height: "100px",
          background: "rgba(255,255,255,0.1)",
          borderRadius: "50%",
          transform: "translate(30%, -30%)",
        },
      }}
      style={{
        background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
        color: "white",
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              {title}
            </Typography>
            {loading ? (
              <Skeleton
                variant="text"
                width={80}
                height={40}
                sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
              />
            ) : (
              <>
                <Typography variant="h4" fontWeight="bold">
                  {value}
                </Typography>
                {subtitle && (
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {subtitle}
                  </Typography>
                )}
              </>
            )}
            {change !== undefined && !loading && (
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                {isPositive ? (
                  <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 16, mr: 0.5 }} />
                )}
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {isPositive ? "+" : ""}
                  {change}% {changeLabel}
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              width: 56,
              height: 56,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

// Connected Users Card
const ConnectedUsersCard = ({ data, loading, onRefresh, refreshing }) => {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Avatar sx={{ bgcolor: "#4caf50", mr: 2 }}>
              <WifiIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">Currently Online</Typography>
              <Typography variant="body2" color="text.secondary">
                Real-time connected users
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onRefresh}
            disabled={refreshing}
            size="small"
            sx={{
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            <RotatingIcon rotating={refreshing} />
          </IconButton>
        </Box>
        <Divider sx={{ my: 2 }} />
        {loading ? (
          <Skeleton variant="rectangular" height={100} />
        ) : (
          <>
            <Typography
              variant="h3"
              fontWeight="bold"
              color="primary"
              gutterBottom
            >
              {data.totalConnected}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
              <Chip
                label={`Users: ${data.users}`}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`Subusers: ${data.subusers}`}
                size="small"
                color="secondary"
                variant="outlined"
              />
              <Chip
                label={`Notaries: ${data.notaries}`}
                size="small"
                color="success"
                variant="outlined"
              />
              <Chip
                label={`Guests: ${data.guests}`}
                size="small"
                color="warning"
                variant="outlined"
              />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Monthly Trend Chart Component
const MonthlyTrendChart = ({ data, title, loading }) => {
  const theme = useTheme();

  const chartOptions = {
    chart: {
      background: "transparent",
      toolbar: { show: false },
    },
    colors: ["#667eea", "#f5576c"],
    dataLabels: { enabled: false },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2,
        stops: [0, 100],
      },
    },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 2,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    stroke: { curve: "smooth", width: 3 },
    theme: { mode: theme.palette.mode },
    xaxis: {
      categories: data?.labels || [],
      labels: {
        style: { colors: theme.palette.text.secondary },
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => Math.round(value),
        style: { colors: theme.palette.text.secondary },
      },
    },
    tooltip: {
      theme: theme.palette.mode,
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
    },
  };

  // Build series from data object (supports mainUsers/subusers, created/notarized, created/recorded)
  const chartSeries = [];
  if (data?.mainUsers && data?.subusers) {
    chartSeries.push(
      { name: "Main Users", data: data.mainUsers },
      { name: "Subusers", data: data.subusers }
    );
  } else if (data?.created && data?.notarized) {
    chartSeries.push(
      { name: "Created Packages", data: data.created },
      { name: "Notarized Packages", data: data.notarized }
    );
  } else if (data?.created && data?.recorded) {
    chartSeries.push(
      { name: "Created NOC", data: data.created },
      { name: "Recorded NOC", data: data.recorded }
    );
  }

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : (
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="area"
            height={300}
          />
        )}
      </CardContent>
    </Card>
  );
};

// Pie Chart Component
const DistributionChart = ({ data, title, loading }) => {
  const theme = useTheme();

  const chartOptions = {
    chart: {
      background: "transparent",
    },
    colors: ["#667eea", "#764ba2", "#f093fb", "#f5576c", "#4facfe", "#43e97b"],
    labels: data?.labels || [],
    legend: {
      position: "bottom",
      labels: {
        colors: theme.palette.text.primary,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${Math.round(val)}%`,
    },
    theme: { mode: theme.palette.mode },
  };

  const chartSeries = data?.values || [];

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {loading ? (
          <Skeleton
            variant="circular"
            width={250}
            height={250}
            sx={{ mx: "auto" }}
          />
        ) : (
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="donut"
            height={300}
          />
        )}
      </CardContent>
    </Card>
  );
};

// Customer Breakdown Card
const CustomerBreakdownCard = ({ mainUsers, subusers, loading }) => {
  const total = mainUsers + subusers;
  const mainPercent = total > 0 ? Math.round((mainUsers / total) * 100) : 0;
  const subPercent = total > 0 ? Math.round((subusers / total) * 100) : 0;

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Avatar sx={{ bgcolor: "#667eea", mr: 2 }}>
            <GroupsIcon />
          </Avatar>
          <Box>
            <Typography variant="h6">Customer Breakdown</Typography>
            <Typography variant="body2" color="text.secondary">
              Main users vs Subusers
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        {loading ? (
          <Skeleton variant="rectangular" height={100} />
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {mainUsers.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Main Users ({mainPercent}%)
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" fontWeight="bold" color="secondary">
                  {subusers.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Subusers ({subPercent}%)
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

const SalesDashboard = () => {
  const { user } = useSalesAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalMainUsers: 0,
    totalSubusers: 0,
    newCustomersThisMonth: 0,
    newMainUsersThisMonth: 0,
    newSubusersThisMonth: 0,
    newCustomersChange: 0,
    documentsCreated: 0,
    documentsChange: 0,
    createdPackages: 0,
    createdPackagesChange: 0,
    notarizedPackages: 0,
    notarizedChange: 0,
    createdNOC: 0,
    createdNOCChange: 0,
    recordedNOC: 0,
    recordedNOCChange: 0,
    activeSubscriptions: 0,
  });
  const [customerTrend, setCustomerTrend] = useState({
    labels: [],
    mainUsers: [],
    subusers: [],
  });
  const [packagesTrend, setPackagesTrend] = useState({
    labels: [],
    created: [],
    notarized: [],
  });
  const [nocTrend, setNocTrend] = useState({
    labels: [],
    created: [],
    recorded: [],
  });
  const [planDistribution, setPlanDistribution] = useState({
    labels: [],
    values: [],
  });
  const [connectedUsers, setConnectedUsers] = useState({
    totalConnected: 0,
    users: 0,
    subusers: 0,
    notaries: 0,
    guests: 0,
    privateProviders: 0,
  });
  const [connectedLoading, setConnectedLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const smDown = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const fetchdataRef = useRef(null);

  const fetchConnectedUsers = useCallback(async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
      } else {
        setConnectedLoading(true);
      }
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/connected-users`,
        { withCredentials: true }
      );
      if (res.data.success) {
        setConnectedUsers(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching connected users:", error);
    } finally {
      setConnectedLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/nocApi/sales/dashboard-stats`,
          { withCredentials: true }
        );

        if (res.data.success) {
          const data = res.data.data;
          setStats({
            totalCustomers: data.totalCustomers || 0,
            totalMainUsers: data.totalMainUsers || 0,
            totalSubusers: data.totalSubusers || 0,
            newCustomersThisMonth: data.newCustomersThisMonth || 0,
            newMainUsersThisMonth: data.newMainUsersThisMonth || 0,
            newSubusersThisMonth: data.newSubusersThisMonth || 0,
            newCustomersChange: data.newCustomersChange || 0,
            documentsCreated: data.documentsCreated || 0,
            documentsChange: data.documentsChange || 0,
            createdPackages: data.createdPackages || 0,
            createdPackagesChange: data.createdPackagesChange || 0,
            notarizedPackages: data.notarizedPackages || 0,
            notarizedChange: data.notarizedChange || 0,
            createdNOC: data.createdNOC || 0,
            createdNOCChange: data.createdNOCChange || 0,
            recordedNOC: data.recordedNOC || 0,
            recordedNOCChange: data.recordedNOCChange || 0,
            activeSubscriptions: data.activeSubscriptions || 0,
          });
          setCustomerTrend(
            data.customerTrend || { labels: [], mainUsers: [], subusers: [] }
          );
          setPackagesTrend(
            data.packagesTrend || { labels: [], created: [], notarized: [] }
          );
          setNocTrend(
            data.nocTrend || { labels: [], created: [], recorded: [] }
          );
          setPlanDistribution(
            data.planDistribution || { labels: [], values: [] }
          );
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        // Set mock data for development
        setStats({
          totalCustomers: 156,
          totalMainUsers: 120,
          totalSubusers: 36,
          newCustomersThisMonth: 23,
          newMainUsersThisMonth: 18,
          newSubusersThisMonth: 5,
          newCustomersChange: 12,
          documentsCreated: 1847,
          documentsChange: 8,
          notarizedPackages: 234,
          notarizedChange: 15,
          activeSubscriptions: 142,
        });
        setCustomerTrend({
          labels: [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ],
          mainUsers: [10, 15, 12, 20, 18, 25, 22, 28, 26, 32, 30, 38],
          subusers: [2, 4, 3, 5, 4, 5, 6, 7, 6, 8, 8, 7],
        });
        setPackagesTrend({
          labels: [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ],
          created: [45, 52, 48, 65, 58, 72, 68, 85, 78, 92, 88, 105],
          notarized: [38, 44, 40, 55, 50, 62, 58, 72, 66, 78, 74, 88],
        });
        setNocTrend({
          labels: [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ],
          created: [42, 48, 45, 60, 55, 68, 64, 80, 72, 85, 82, 98],
          recorded: [35, 40, 38, 50, 46, 58, 54, 68, 62, 72, 68, 82],
        });
        setPlanDistribution({
          labels: ["Starter", "Premium", "Rockstar", "Enterprise"],
          values: [35, 40, 20, 5],
        });
      } finally {
        setLoading(false);
      }
    };

    if (!fetchdataRef.current) {
      fetchdataRef.current = true;
      fetchDashboardData();
      fetchConnectedUsers();
    }
  }, [fetchConnectedUsers]);

  return (
    <Box sx={{ p: smDown ? 1 : 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom color="primary">
          Sales Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back, {user?.full_name || "Sales Team"}! Here's your
          performance overview.
        </Typography>
      </Box>

      {/* Top Stat Cards */}
      <Grid container spacing={smDown ? 1 : 2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Total Customers"
            value={stats.totalCustomers.toLocaleString()}
            subtitle={`${stats.totalMainUsers} users + ${stats.totalSubusers} subusers`}
            icon={<PeopleIcon />}
            color="#667eea"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="New This Month"
            value={stats.newCustomersThisMonth.toLocaleString()}
            subtitle={`${stats.newMainUsersThisMonth} users + ${stats.newSubusersThisMonth} subusers`}
            change={stats.newCustomersChange}
            changeLabel="vs last month"
            icon={<PersonAddIcon />}
            color="#11998e"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Documents Created"
            value={stats.documentsCreated.toLocaleString()}
            change={stats.documentsChange}
            changeLabel="vs last month"
            icon={<DescriptionIcon />}
            color="#f093fb"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Notarized Packages"
            value={stats.notarizedPackages.toLocaleString()}
            change={stats.notarizedChange}
            changeLabel="vs last month"
            icon={<VerifiedIcon />}
            color="#f5576c"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Second Row - Connected Users & Customer Breakdown */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ConnectedUsersCard
            data={connectedUsers}
            loading={connectedLoading}
            onRefresh={() => fetchConnectedUsers(true)}
            refreshing={refreshing}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CustomerBreakdownCard
            mainUsers={stats.totalMainUsers}
            subusers={stats.totalSubusers}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <MonthlyTrendChart
            data={customerTrend}
            title="New Customers: Main Users vs Subusers"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <MonthlyTrendChart
            data={packagesTrend}
            title="Created Packages vs Notarized Packages"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <MonthlyTrendChart
            data={nocTrend}
            title="Created NOC vs Recorded NOC"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <DistributionChart
            data={planDistribution}
            title="Subscription Distribution"
            loading={loading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesDashboard;
